import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { Button as Button95, Hourglass, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership, UNKNOWN_COMMUNITY_MEMBERSHIP } from "@/types/community_membership";
import { RelativePathString, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import CancelJoin from '@/components/CancelJoin';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  inputWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
    minWidth: AppConsts.BASIC_MINIMUM_WIDTH,
    maxWidth: AppConsts.MAX_WIDTH_COMFORTABLE_READING,
  }
});

export default function Email() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const emailInput = useRef<RNTextInput>(null);
  const router = useRouter();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const dispatch = useDispatch();

  const { register, control, handleSubmit, setValue, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      email: '',
    }
  });

  const [email] = watch(["email"]);

  const onSubmit = useCallback(async (data: any) => {
    if (communityMembership.uniqueid != UNKNOWN_COMMUNITY_MEMBERSHIP.uniqueid &&
        data.email == communityMembership.user_info.email) {
      // User pressed the back button after successfully setting their email and the
      // email they've submitted is the same, so we just treat that as a success.
      router.navigate(AppUtils.getProfileCreateNextPageLink(communityMembership) as RelativePathString);
    } else {
      try {
        showSpinner();

        const response = await AppUtilities.fetchAuthorized(
          "Email.onSubmit",
          "/rabbl/api/set_email",
          "POST",
          {
            domain: domain,
            email: data.email,
          }
        );
        const membership: CommunityMembership = ((response as any).body as any).community_membership;
        dispatch(setData(response));
        router.navigate(AppUtils.getProfileCreateNextPageLink(membership) as RelativePathString);

      } catch (e) {
        AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
        AppUtilities.handleException(e, "Email.onSubmit");
      } finally {
        hideSpinner();
      }
    }
  }, [domain, communityMembership]);

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (emailInput && emailInput.current) {
        emailInput.current?.focus();
      }
    }
  }, [emailInput]);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Your email"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>
          <AppText>Enter your email</AppText>

          <Controller
            control={control}
            name="email"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                ref={emailInput}
                value={value}
                onChangeText={onChange}
                placeholder="Email"
                style={styles.formElement}
                returnKeyType="next"
                onBlur={onBlur}
                autoComplete="email"
                keyboardType="email-address"
                textContentType="emailAddress"
                onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
                autoCapitalize='none'
              />
            )}
          />
          {errors.email && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^^ Email is required.</AppText>}

          <View style={styles.inputWrapper}>
            <Button95
              onPress={handleSubmit(onSubmit, onValidationErrors)}
              disabled={email == ''}
            >Next</Button95>
          </View>
        </View>
      </Panel>

      <CancelJoin />
      
      <Footer />
    </BasicScrollView>
  );
}
